<template>
  <el-card>
    <div id="page-panel">
      <div class="customer-top-nav">
        <div class="title">
          <span>{{ $t('reports.names.' + reportCode) }}</span><br>
        </div>
        <div class="controls no-print">
          <el-switch
            v-model="expandAll"
            active-text="Développer tout"
            inactive-text=""
            @change="toggleExpandAll">
          </el-switch>
          <el-dropdown size="small" type="default" class="">
            <el-button type="primary" size="small">
              <i class="el-icon-download"></i>
            </el-button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item @click.native="downloadExcel"><i class="mdi mdi-file-excel"/> Excel</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
          <el-button type="primary" @click="openFilterDialog" size="small"><i class="mdi mdi-filter"></i></el-button>
        </div>
      </div>

      <p>
        <span class="fs-14 secondary-text">{{ companyName }}</span><br>
        <span class="fs-14 secondary-text">{{ currentFilterPeriod }}</span><br>
      </p>

      <div id="printMe">
        <el-row>
          <el-table
            ref="customerTable"
            :data="groupedCustomers"
            :style="tableWidth"
            :default-expand-all="expandAll"
            :default-sort="{ prop: 'total_qty', order: 'descending' }"
            empty-text="Il n'y a aucune vente pour cette période"
            class="fs-10"
            :row-style="mainRowStyle">
            <el-table-column type="expand">
              <template slot-scope="props">
                <div class="expand-content">
                  <el-table
                    :data="props.row.products"
                    size="mini"
                    border
                    class="customer-summary-table">
                    <el-table-column label="Produit" min-width="280">
                      <template slot-scope="productScope">
                        <div class="product-name">{{ productScope.row.product_name }} <span class="product-provider" v-if="productScope.row.provider_name"> - {{ productScope.row.provider_name }}</span></div>
                        <div class="product-sku">{{ productScope.row.sku }}</div>
                        <div class="product-meta">{{ productScope.row.uvc || '-' }} | {{ productScope.row.volume || '-' }} ml</div>
                      </template>
                    </el-table-column>
                    <el-table-column label="UVC" width="80" align="right">
                      <template slot-scope="productScope">
                        {{ formatQty(productScope.row.uvc) }}
                      </template>
                    </el-table-column>
                    <el-table-column label="Format" min-width="120">
                      <template slot-scope="productScope">
                        {{ productScope.row.format || '-' }}
                      </template>
                    </el-table-column>
                    <el-table-column label="Volume (ml)" width="110" align="right">
                      <template slot-scope="productScope">
                        {{ formatQty(productScope.row.volume) }}
                      </template>
                    </el-table-column>
                    <el-table-column label="Quantité" width="120" align="right" sortable>
                      <template slot-scope="productScope">
                        {{ formatQty(productScope.row.total_qty) }}
                      </template>
                    </el-table-column>
                    <el-table-column label="Nb caisses" width="120" align="right">
                      <template slot-scope="productScope">
                        {{ formatQty(productScope.row.total_qty_9l) }}
                      </template>
                    </el-table-column>
                  </el-table>
                </div>
              </template>
            </el-table-column>

            <el-table-column prop="customer_id" label="No client" width="120" />

            <el-table-column label="Client" min-width="260">
              <template slot-scope="scope">
                <PortausLink
                  :id="scope.row.customer_id"
                  path="/customers/"
                  :label="scope.row.customer_name"
                  fs="fs-12"
                  :openNewTab="true" />
              </template>
            </el-table-column>

            <el-table-column prop="total_qty" label="Total quantité vendue" width="180" align="right" sortable>
              <template slot-scope="scope">
                <strong>{{ formatQty(scope.row.total_qty) }}</strong>
              </template>
            </el-table-column>

            <el-table-column prop="total_cases" label="Total nb caisses" width="160" align="right" sortable>
              <template slot-scope="scope">
                <strong>{{ formatQty(scope.row.total_cases) }}</strong>
              </template>
            </el-table-column>
          </el-table>
        </el-row>
      </div>

      <reportFilter
        v-if="!loading"
        :visible="reportFilterVisible"
        @close="closeFilterDialog"
        :predefinedPeriod="predefinedPeriod"
        :period="period"
        :startDate="filters.startDate"
        :endDate="filters.endDate"
        :customerFilter="true"
        :customer="selectedCustomer"
        :providerFilter="true"
        :provider="selectedProvider"
        :reps="salesRep"
        :selectedReps="getUserById(filters.reps)"
        :customer_classes="customerClasses"
        :selectedCustomerClasses="filters.customer_classes"
        :productFilter="true"
        :selectedProduct="filters.product"
        @applyFilter="handleFilter" />

      <WatingDialog :visible="loading" content="Veuillez patienter pendant que nous préparons votre rapport. Cela peut prendre plusieurs secondes."/>
    </div>
  </el-card>
</template>

<script>
import _ from 'lodash';
import salesAPI from '@/services/API/sales.js';
import moment from 'moment';
import XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import reportFilter from './report-filter';
import reportUtil from '@/utils/report-util.js';
import { mapActions, mapState, mapGetters } from 'vuex';
import Promise from 'bluebird';
import WatingDialog from '@/components/waiting-dialog';
import PortausLink from '@/components/portaus-link';

export default {
  name: 'SummaryProductsPurchasedByCustomerReport',
  props: {
    width: { type: Number, default: 100 },
    fiscalEndDay: Number,
    fiscalEndMonth: Number,
    lastFiscalYearDate: Object,
    fiscalYearDate: Object,
    month: Object,
    lastMonth: Object,
    companyName: '',
  },
  data() {
    return {
      data: [],
      loading: true,
      filters: {
        range: 'MONTH',
        startDate: null,
        endDate: null,
        statuses: [],
        customerId: null,
        providerId: null,
        reps: [],
        products: [],
      },
      reportFilterVisible: false,
      expandAll: true,
    };
  },
  components: {
    reportFilter,
    WatingDialog,
    PortausLink,
  },
  computed: {
    selectedCustomer() {
      if (this.filters.customerId != null) return { id: this.filters.customerId };
      return null;
    },
    selectedProvider() {
      if (this.filters.providerId != null) return { id: this.filters.providerId };
      return null;
    },
    predefinedPeriod() {
      if (this.fiscalYearDate.startDate != null && this.fiscalYearDate.endDate != null) {
        return [
          { code: 'MONTH', description: this.month.startDate.format('DD MMM YYYY') + '-' + this.month.endDate.format('DD MMM YYYY') },
          { code: 'LAST_MONTH', description: this.lastMonth.startDate.format('DD MMM YYYY') + '-' + this.lastMonth.endDate.format('DD MMM YYYY') },
          { code: 'LAST_12_MONTHS', description: moment().subtract(11, 'months').startOf('month').format('DD MMM YYYY') + ' - ' + moment().endOf('month').format('DD MMM YYYY') },
          { code: 'FISCAL_YEAR', description: this.fiscalYearDate.startDate.format('DD MMM YYYY') + '-' + this.fiscalYearDate.endDate.format('DD MMM YYYY') },
          { code: 'LAST_FISCAL_YEAR', description: this.lastFiscalYearDate.startDate.format('DD MMM YYYY') + '-' + this.lastFiscalYearDate.endDate.format('DD MMM YYYY') },
          { code: 'CUSTOM', range: true },
        ];
      }
      return [];
    },
    tableWidth() {
      return 'width : ' + this.width + 'px;';
    },
    currentFilterPeriod() {
      return reportUtil.currentFilterPeriod(this.fiscalYearDate, this.lastFiscalYearDate, this.filters.range, this.filters.startDate, this.filters.endDate);
    },
    reportCode() {
      return this.$route.params.code;
    },
    period() {
      return _.find(this.predefinedPeriod, p => p.code === this.filters.range);
    },
    groupedCustomers() {
      if (!Array.isArray(this.data)) return [];

      return this.data
        .map((row) => {
          let products = _.get(row, 'products', []);
          if (_.isString(products)) {
            try {
              products = JSON.parse(products);
            } catch (e) {
              products = [];
            }
          }

          const normalizedProducts = _.map(products || [], p => ({
            product_id: _.get(p, 'product_id'),
            product_name: _.get(p, 'product_name'),
            sku: _.get(p, 'sku'),
            provider_name: _.get(p, 'provider_name'),
            uvc: Number(_.get(p, 'uvc', 0)) || 0,
            format: _.get(p, 'format'),
            volume: Number(_.get(p, 'volume', 0)) || 0,
            total_qty: Number(_.get(p, 'total_qty', 0)) || 0,
            total_qty_9l: Number(_.get(p, 'total_qty_9l', 0)) || 0,
          })).sort((a, b) => {
            const qtyDiff = Number(b.total_qty || 0) - Number(a.total_qty || 0);
            if (qtyDiff !== 0) return qtyDiff;
            if ((a.product_name || '') < (b.product_name || '')) return -1;
            if ((a.product_name || '') > (b.product_name || '')) return 1;
            return 0;
          });

          return {
            customer_id: row.customer_id,
            customer_name: row.customer_name,
            total_qty: Number(row.total_qty || 0),
            total_cases: Number(row.total_qty_9l || 0),
            products: normalizedProducts,
          };
        })
        .sort((a, b) => {
          const qtyDiff = Number(b.total_qty || 0) - Number(a.total_qty || 0);
          if (qtyDiff !== 0) return qtyDiff;
          if ((a.customer_name || '') < (b.customer_name || '')) return -1;
          if ((a.customer_name || '') > (b.customer_name || '')) return 1;
          return 0;
        });
    },
    salesRep() {
      return this.filterUserWithRoleRep;
    },
    ...mapState('inventory', ['inventories']),
    ...mapGetters('users', ['filterUserWithRoleRep', 'getUserById']),
    ...mapState('controlData', ['customerClasses']),
    ...mapGetters(['isStrictFilteredByRep', 'isCurrentUserRep', 'getCurrentUserId', 'isCurrentUserAdmin']),
  },
  methods: {
    mainRowStyle() {
      return { backgroundColor: '#f2f2f2' };
    },
    toggleExpandAll(value) {
      this.groupedCustomers.forEach(row => {
        this.$refs.customerTable.toggleRowExpansion(row, value);
      });
    },
    handleFilter(filters) {
      localStorage.sales_range = filters.period != null ? filters.period.code : 'MONTH';

      if (filters.period != null && filters.period.code === 'CUSTOM' && filters.dateRange != null && filters.dateRange.length === 2) {
        localStorage.sales_startDate = filters.dateRange[0];
        localStorage.sales_endDate = filters.dateRange[1];
      }

      const inventories = [];
      _.forEach(filters.inventories, m => inventories.push(m.id));
      localStorage.sales_inventories = JSON.stringify(inventories);

      localStorage.sales_customerId = filters.customer != null ? filters.customer.id : undefined;
      localStorage.sales_providerId = filters.provider != null ? filters.provider.id : undefined;

      const reps = [];
      _.forEach(filters.reps, m => reps.push(m.id));
      localStorage.sales_reps = JSON.stringify(reps);

      localStorage.sales_product = JSON.stringify(filters.product);

      const customerClasses = [];
      _.forEach(filters.customer_classes, m => customerClasses.push(m.id));
      localStorage.sales_customer_classes = JSON.stringify(customerClasses);

      this.setFilters();
      this.fetchData();
    },
    formatQty(value) {
      const parsed = Number(value || 0);
      return Math.round(parsed * 100) / 100;
    },
    openFilterDialog() {
      this.reportFilterVisible = true;
    },
    closeFilterDialog() {
      this.reportFilterVisible = false;
    },
    downloadExcel() {
      const rows = [];
      let totalQty = 0;
      let totalCases = 0;

      this.groupedCustomers.forEach((customer) => {
        totalQty += Number(customer.total_qty || 0);
        totalCases += Number(customer.total_cases || 0);

        rows.push({
          'No client': customer.customer_id,
          'Client': customer.customer_name,
          'Produit': '',
          'SKU': '',
          'Fournisseur': '',
          'UVC': '',
          'Format': '',
          'Volume (ml)': '',
          'Quantité': this.formatQty(customer.total_qty),
          'Nb caisses': this.formatQty(customer.total_cases),
        });

        customer.products.forEach((product) => {
          rows.push({
            'No client': '',
            'Client': '',
            'Produit': product.product_name,
            'SKU': product.sku,
            'Fournisseur': product.provider_name,
            'UVC': this.formatQty(product.uvc),
            'Format': product.format,
            'Volume (ml)': this.formatQty(product.volume),
            'Quantité': this.formatQty(product.total_qty),
            'Nb caisses': this.formatQty(product.total_qty_9l),
          });
        });
      });

      rows.push({
        'No client': '',
        'Client': 'TOTAL',
        'Produit': '',
        'SKU': '',
        'Fournisseur': '',
        'UVC': '',
        'Format': '',
        'Volume (ml)': '',
        'Quantité': Math.round(totalQty * 100) / 100,
        'Nb caisses': Math.round(totalCases * 100) / 100,
      });

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Produits achetes par client');
      const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
      const filename = `produits-achetes-par-client-${moment().format('YYYY-MM-DD')}.xlsx`;
      saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), filename);
    },
    prepareBuildRequest() {
      const filters = _.cloneDeep(this.filters);
      if (filters.startDate != null) filters.startDate = moment(filters.startDate).format('YYYY-MM-DD Z');
      if (filters.endDate != null) filters.endDate = moment(filters.endDate).format('YYYY-MM-DD Z');
      filters.productId = filters.product != null ? filters.product.id : undefined;
      delete filters.product;
      return filters;
    },
    fetchData() {
      this.loading = true;
      const filters = this.prepareBuildRequest();
      return salesAPI.getSummaryProductsPurchasedByCustomer(filters)
        .then((data) => {
          this.data = data;
          this.loading = false;
        })
        .catch((err) => {
          console.log(err);
          this.loading = false;
          this.$message.error('Une erreur système est survenue. Veuillez réessayer plus tard');
        });
    },
    setFilters() {
      if (localStorage.sales_range == null) localStorage.sales_range = 'MONTH';
      this.filters.range = localStorage.sales_range;

      if (this.filters.range === 'FISCAL_YEAR' && this.fiscalYearDate.startDate != null && this.fiscalYearDate.endDate != null) {
        localStorage.sales_startDate = this.fiscalYearDate.startDate;
        localStorage.sales_endDate = this.fiscalYearDate.endDate;
      } else if (this.filters.range === 'LAST_FISCAL_YEAR' && this.lastFiscalYearDate.startDate != null && this.lastFiscalYearDate.endDate != null) {
        localStorage.sales_startDate = this.lastFiscalYearDate.startDate;
        localStorage.sales_endDate = this.lastFiscalYearDate.endDate;
      } else if (this.filters.range === 'MONTH') {
        localStorage.sales_startDate = this.month.startDate;
        localStorage.sales_endDate = this.month.endDate;
      } else if (this.filters.range === 'LAST_MONTH') {
        localStorage.sales_startDate = this.lastMonth.startDate;
        localStorage.sales_endDate = this.lastMonth.endDate;
      } else if (this.filters.range === 'LAST_12_MONTHS') {
        localStorage.sales_startDate = moment().subtract(11, 'months').startOf('month');
        localStorage.sales_endDate = moment().endOf('month');
      }

      this.filters.startDate = localStorage.sales_startDate;
      this.filters.endDate = localStorage.sales_endDate;
      this.filters.customerId = localStorage.sales_customerId !== 'undefined' && localStorage.sales_customerId != null ? parseInt(localStorage.sales_customerId) : undefined;
      this.filters.providerId = localStorage.sales_providerId !== 'undefined' && localStorage.sales_providerId != null ? parseInt(localStorage.sales_providerId) : undefined;
      this.filters.reps = localStorage.sales_reps != null ? JSON.parse(localStorage.sales_reps) : [];
      this.filters.product = localStorage.sales_product != null ? JSON.parse(localStorage.sales_product) : null;
      this.filters.customer_classes = localStorage.sales_customer_classes != null ? JSON.parse(localStorage.sales_customer_classes) : [];
      this.filters.inventories = localStorage.sales_inventories != null ? JSON.parse(localStorage.sales_inventories) : [];

      this.applyStrictRepFilter();
    },
    applyStrictRepFilter() {
      if (this.isStrictFilteredByRep === true && this.isCurrentUserRep === true && !this.isCurrentUserAdmin && this.getCurrentUserId != null) {
        this.filters.reps = [this.getCurrentUserId];
        localStorage.sales_reps = JSON.stringify(this.filters.reps);
      }
    },
    ...mapActions('inventory', ['fetchInventories']),
    ...mapActions('users', ['fetchAdminUsers']),
    ...mapActions('controlData', ['fetchCustomerClasses']),
  },
  beforeDestroy() {
    this.data = [];
  },
  created() {
    this.loading = true;
    return Promise.all([
      this.fetchAdminUsers(),
      this.fetchInventories(),
      this.fetchCustomerClasses(),
    ])
      .then(() => this.setFilters())
      .then(() => this.fetchData());
  },
};
</script>

<style lang="scss" scoped>
@import '../../../../../assets/scss/_variables';

@media print {
  body, html {
    margin-top: 0%;
    font-size: 0.8em;
    display: block;
    height: 100%;
    width: 100%;
    position: unset !important;
    background: none;
    overflow-x: hidden;
    overflow-y: auto;
  }
  .no-print {
    display: none;
  }
  .el-card {
    border: none;
    width: 100%;
  }

  @page {
    size: portrait;
  }
}

.title {
  font-size: 1.6em;
  font-weight: bold;
  margin-right: 20px;
  position: relative;
  float: left;
}

.customer-top-nav {
  height: 40px;
}

.controls {
  position: relative;
  float: right;
  display: flex;
  align-items: center;
  gap: 8px;

  button {
    margin-left: 20px;
  }
}

.product-name,
.product-sku {
  line-height: 1.5;
  margin: 0;
}

.product-provider {
  line-height: 1.5;
  margin: 0;
  font-size: 12px;
  color: #606266;
}

.product-name {
  font-size: 14px;
  font-weight: bold;
}

.product-meta {
  line-height: 1.5;
  margin: 0;
  color: #606266;
}

.product-sku {
  font-size: 12px;
}

.customer-summary-table {
  margin: 4px 0;
}

.expand-content {
  padding: 20px;
}
</style>
